import { useCallback, useMemo, useState } from 'react'

import useSubscriptionSettingsTracking from '../../hooks/useSubscriptionSettingsTracking'

const OTHER_VALUE = 'other'
const OTHER_PREFIX = 'other: '
const MAX_CUSTOM_REASON_LENGTH = 500 - OTHER_PREFIX.length
const PLAN_MONTHLY = 'monthly'

function parseAmount(value) {
  if (typeof value === 'number') return value

  return parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0
}

function formatAccessDate(isoDate, locale = 'en') {
  return new Date(isoDate).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function useCancelModal({
  onClose,
  onSubmit,
  subscriptionId,
  feeData,
  feeError,
  locale,
  planType = 'yearly',
}) {
  const [selectedReason, setSelectedReason] = useState(null)
  const [otherText, setOtherText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isAcknowledged, setIsAcknowledged] = useState(false)
  const { trackCancellationConfirmed } = useSubscriptionSettingsTracking()

  const isOtherSelected = selectedReason?.value === OTHER_VALUE

  const isMonthly = planType === PLAN_MONTHLY

  const feeBreakdown = useMemo(() => {
    if (!feeData) return null

    const cancelFee = parseAmount(feeData.cancel_fee)
    if (!cancelFee) return null

    return {
      remainingMonths: feeData.remaining_months,
      monthlyRate: parseAmount(feeData.monthly_rate),
      totalRemaining: parseAmount(feeData.total_remaining),
      cancelFee,
    }
  }, [feeData])

  const hasFee = Boolean(feeBreakdown) && !isMonthly

  const reasonSelectPosition = hasFee ? 'top' : 'below-banner'

  const accessUntilFormatted = useMemo(() => {
    if (!feeData) return null

    return formatAccessDate(feeData.access_until, locale)
  }, [feeData, locale])

  const handleClose = useCallback(() => {
    setSelectedReason(null)
    setOtherText('')
    setIsSubmitting(false)
    setIsAcknowledged(false)
    onClose()
  }, [onClose])

  const handleReasonChange = useCallback((option) => {
    setSelectedReason(option)

    if (option?.value !== OTHER_VALUE) {
      setOtherText('')
    }
  }, [])

  const handleOtherTextChange = useCallback((e) => {
    setOtherText(e.target.value)
  }, [])

  const submitAndTrack = useCallback(async (reason, trackingArgs) => {
    setIsSubmitting(true)

    try {
      await onSubmit(reason)
    } catch {
      setIsSubmitting(false)
      return
    }

    trackCancellationConfirmed(...trackingArgs)
    handleClose()
  }, [onSubmit, trackCancellationConfirmed, handleClose])

  const resolveReasonPayload = useCallback(() => {
    if (!selectedReason) return null

    const isOther = selectedReason.value === OTHER_VALUE
    const customReason = otherText.trim()
    if (isOther && !customReason) return null

    return {
      reason: isOther ? `${OTHER_PREFIX}${customReason}` : selectedReason.value,
      trackingArgs: [
        selectedReason.value,
        subscriptionId,
        isOther ? customReason : undefined,
      ],
    }
  }, [selectedReason, otherText, subscriptionId])

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return

    if (hasFee && !isAcknowledged) return

    const payload = resolveReasonPayload()
    if (!payload) return

    const reason = hasFee
      ? `early_cancellation_fee: ${payload.reason}`
      : payload.reason
    const trackingArgs = hasFee
      ? ['early_cancellation_fee', subscriptionId, payload.reason]
      : payload.trackingArgs

    await submitAndTrack(reason, trackingArgs)
  }, [
    hasFee,
    isAcknowledged,
    isSubmitting,
    resolveReasonPayload,
    submitAndTrack,
    subscriptionId,
  ])

  const isReasonMissing = !selectedReason || (isOtherSelected && !otherText.trim())
  const isAckMissing = hasFee && !isAcknowledged
  const isSubmitDisabled = !!feeError || isSubmitting || isReasonMissing || isAckMissing

  return {
    feeBreakdown,
    hasFee,
    feeError,
    reasonSelectPosition,
    accessUntilFormatted,
    isAcknowledged,
    setIsAcknowledged,
    selectedReason,
    isOtherSelected,
    otherText,
    handleReasonChange,
    handleOtherTextChange,
    handleSubmit,
    handleClose,
    isSubmitDisabled,
    maxCustomReasonLength: MAX_CUSTOM_REASON_LENGTH,
  }
}

export default useCancelModal
